import app from '../config/firebase';
import { salesRecoveryApi } from './api';
import { OrderItem, SalesRecoveryCaptureEvent, SalesRecoveryOpportunityCaptureRequestDto } from '../types';

const CART_CODE_KEY = 'sales-recovery-cart-code';
const SESSION_ID_KEY = 'sales-recovery-session-id';
const NOTIFICATION_PROMPT_KEY = 'sales-recovery-notification-prompted';
const PENDING_CAPTURES_KEY = 'sales-recovery-pending-captures';
const MAX_PENDING_CAPTURES = 20;
const CAPTURE_ENDPOINT = `${process.env.REACT_APP_PAYMENTS_API_URL || (process.env.NODE_ENV === 'development' ? 'http://localhost:8084/app/payments' : '')}/v1/sales-recovery/opportunities`;

export const getSalesRecoveryNotificationPermission = (): NotificationPermission | 'unsupported' => {
  if (typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) {
    return 'unsupported';
  }
  return Notification.permission;
};

export const getSalesRecoveryPushSetupIssue = (): string | null => {
  if (typeof window === 'undefined') return 'Notificações não estão disponíveis neste ambiente.';
  if (!('Notification' in window)) return 'Este navegador não suporta notificações.';
  if (!('serviceWorker' in navigator)) return 'Este navegador não suporta service worker para push.';
  if (!window.isSecureContext) return 'Notificações push exigem HTTPS ou localhost.';
  if (!app) return 'Firebase não está configurado no cardápio web.';
  if (!process.env.REACT_APP_FIREBASE_VAPID_KEY) return 'A chave Web Push do Firebase ainda não foi configurada.';
  return null;
};

const randomId = () => {
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID();
  }
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const getSalesRecoveryCartCode = (): string => {
  const existing = localStorage.getItem(CART_CODE_KEY);
  if (existing) return existing;

  const cartCode = randomId();
  localStorage.setItem(CART_CODE_KEY, cartCode);
  return cartCode;
};

export const resetSalesRecoveryCartCode = () => {
  localStorage.removeItem(CART_CODE_KEY);
};

export const getSalesRecoverySessionId = (): string => {
  const existing = localStorage.getItem(SESSION_ID_KEY);
  if (existing) return existing;

  const sessionId = randomId();
  localStorage.setItem(SESSION_ID_KEY, sessionId);
  return sessionId;
};

const getStoredCustomer = () => {
  try {
    const storedUser = localStorage.getItem('user');
    if (!storedUser) return undefined;
    const customer = JSON.parse(storedUser);
    return {
      id: customer.id,
      name: customer.name || [customer.firstName, customer.lastName].filter(Boolean).join(' ') || undefined,
      phone: customer.phone,
      email: customer.email
    };
  } catch (error) {
    console.warn('[SalesRecovery] Could not parse stored customer:', error);
    return undefined;
  }
};

const getPendingCaptures = (): SalesRecoveryOpportunityCaptureRequestDto[] => {
  try {
    return JSON.parse(localStorage.getItem(PENDING_CAPTURES_KEY) || '[]');
  } catch (error) {
    console.warn('[SalesRecovery] Could not parse pending captures:', error);
    return [];
  }
};

const setPendingCaptures = (requests: SalesRecoveryOpportunityCaptureRequestDto[]) => {
  localStorage.setItem(PENDING_CAPTURES_KEY, JSON.stringify(requests.slice(-MAX_PENDING_CAPTURES)));
};

const enqueuePendingCapture = (request: SalesRecoveryOpportunityCaptureRequestDto) => {
  const pending = getPendingCaptures()
    .filter(item => item.cartCode !== request.cartCode || item.eventType !== request.eventType);
  pending.push(request);
  setPendingCaptures(pending);
};

export const flushPendingSalesRecoveryCaptures = async () => {
  const pending = getPendingCaptures();
  if (pending.length === 0) return;

  const failed: SalesRecoveryOpportunityCaptureRequestDto[] = [];
  for (const request of pending) {
    try {
      await salesRecoveryApi.captureOpportunity(request);
    } catch (error) {
      failed.push(request);
      console.warn('[SalesRecovery] Could not retry pending capture:', error);
    }
  }
  setPendingCaptures(failed);
};

export const buildCartRecoveryCaptureRequest = (
  merchantId: string,
  items: OrderItem[],
  total: number,
  couponCode?: string | null,
  customer?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
  },
  eventType: SalesRecoveryCaptureEvent = 'CART_UPDATED'
): SalesRecoveryOpportunityCaptureRequestDto | null => {
  if (!merchantId || items.length === 0) return null;

  return {
    merchantId,
    cartCode: getSalesRecoveryCartCode(),
    sessionId: getSalesRecoverySessionId(),
    customerId: customer?.id,
    customerName: customer?.name,
    customerPhone: customer?.phone,
    customerEmail: customer?.email,
    source: 'CART',
    eventType,
    items: items.map(item => ({
      productId: item.menuItemId,
      name: item.name,
      quantity: item.quantity,
      price: item.price,
      total: item.price * item.quantity
    })),
    subtotal: items.reduce((sum, item) => sum + (item.price * item.quantity), 0),
    total,
    couponCode
  };
};

export const captureCartRecoveryOpportunity = async (
  merchantId: string,
  items: OrderItem[],
  total: number,
  couponCode?: string | null,
  customer?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
  }
) => {
  const request = buildCartRecoveryCaptureRequest(merchantId, items, total, couponCode, customer || getStoredCustomer(), 'CART_UPDATED');
  if (!request) return null;

  try {
    await flushPendingSalesRecoveryCaptures();
    return await salesRecoveryApi.captureOpportunity(request);
  } catch (error) {
    enqueuePendingCapture(request);
    throw error;
  }
};

export const captureCartRecoveryOpportunityOnUnload = (
  merchantId: string,
  items: OrderItem[],
  total: number,
  couponCode?: string | null,
  customer?: {
    id?: string;
    name?: string;
    phone?: string;
    email?: string;
  }
) => {
  const request = buildCartRecoveryCaptureRequest(merchantId, items, total, couponCode, customer || getStoredCustomer(), 'PAGE_CLOSED');
  if (!request || !CAPTURE_ENDPOINT) return false;

  const body = JSON.stringify(request);
  if (typeof navigator !== 'undefined' && navigator.sendBeacon) {
    if (navigator.sendBeacon(CAPTURE_ENDPOINT, body)) {
      return true;
    }
  }

  if (typeof fetch !== 'undefined') {
    fetch(CAPTURE_ENDPOINT, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body,
      keepalive: true
    }).catch(error => {
      enqueuePendingCapture(request);
      console.warn('[SalesRecovery] Could not flush cart recovery opportunity:', error);
    });
    return true;
  }

  enqueuePendingCapture(request);
  return false;
};

export const requestBrowserRecoveryNotifications = async (merchantId: string, forcePrompt = false) => {
  if (!merchantId || typeof window === 'undefined' || !('Notification' in window) || !('serviceWorker' in navigator)) return null;
  if (Notification.permission === 'denied') return null;
  if (!forcePrompt && Notification.permission === 'default' && localStorage.getItem(NOTIFICATION_PROMPT_KEY) === 'true') return null;
  if (!window.isSecureContext) {
    throw new Error('Notificações push exigem HTTPS ou localhost.');
  }

  const permission = Notification.permission === 'granted'
    ? 'granted'
    : await Notification.requestPermission();
  localStorage.setItem(NOTIFICATION_PROMPT_KEY, 'true');
  if (permission !== 'granted') return null;

  const vapidKey = process.env.REACT_APP_FIREBASE_VAPID_KEY;
  if (!app || !vapidKey) {
    throw new Error(getSalesRecoveryPushSetupIssue() || 'Firebase messaging is not configured.');
  }

  const [{ getMessaging, getToken, isSupported }] = await Promise.all([
    import('firebase/messaging')
  ]);

  if (!(await isSupported())) return null;

  const registration = await navigator.serviceWorker.register('/firebase-messaging-sw.js');
  const token = await getToken(getMessaging(app), {
    vapidKey,
    serviceWorkerRegistration: registration
  });

  if (!token) return null;

  return salesRecoveryApi.updatePushToken(getSalesRecoveryCartCode(), {
    merchantId,
    pushToken: token,
    pushOptIn: true
  });
};
